import { useEffect, useRef } from 'react';
import { useAnimationFrame } from 'hooks/useAnimationFrame';
import type { TimeFrame } from 'components/AnimatedTimelineControl';
import type { TerrierOverlay } from '../vendor/terrier';

type UseRadarAnimationOptions = {
  overlay: TerrierOverlay | null;
  frames: TimeFrame[];
  isPlaying: boolean;
  currentFrameIndex: number;
  animationPeriod: number; // total seconds for one full loop
  onFrameAdvance: (index: number) => void;
};

export const useRadarAnimation = ({
  overlay,
  frames,
  isPlaying,
  currentFrameIndex,
  animationPeriod,
  onFrameAdvance,
}: UseRadarAnimationOptions): void => {
  const onFrameAdvanceRef = useRef(onFrameAdvance);
  onFrameAdvanceRef.current = onFrameAdvance;

  const framesRef = useRef(frames);
  framesRef.current = frames;

  const animationStateRef = useRef<{
    msUntilNextFrame: number;
    currentIndex: number;
  } | null>(null);

  /**
   * Compute the animation duration for a given frame transition,
   * proportional to the real-time gap between frames relative to the
   * total time range.
   */
  const getFrameDurationMs = (
    index: number,
    allFrames: TimeFrame[],
    period: number,
  ): number => {
    if (allFrames.length < 2) return period * 1000;
    const totalTimeMs =
      allFrames[allFrames.length - 1].timestamp.getTime() -
      allFrames[0].timestamp.getTime();
    if (totalTimeMs <= 0) return (period * 1000) / allFrames.length;

    const nextIndex = (index + 1) % allFrames.length;
    // Wrapping from last to first: use a small fixed pause
    if (nextIndex === 0) return 500;
    const gapMs =
      allFrames[nextIndex].timestamp.getTime() -
      allFrames[index].timestamp.getTime();
    return (gapMs / totalTimeMs) * period * 1000;
  };

  // Initialize animation state when playback starts
  useEffect(() => {
    if (!isPlaying) {
      animationStateRef.current = null;
      return;
    }

    if (!overlay || frames.length === 0) return;

    const isAtEnd = currentFrameIndex >= frames.length - 1;
    const startIndex = isAtEnd ? 0 : currentFrameIndex;

    animationStateRef.current = {
      msUntilNextFrame: getFrameDurationMs(startIndex, frames, animationPeriod),
      currentIndex: startIndex,
    };

    // Sync Terrier to starting frame (important when wrapping from end)
    if (isAtEnd) {
      const epochSec = frames[startIndex].timestamp.getTime() / 1000;
      overlay.setCurrentTime(epochSec);
      onFrameAdvanceRef.current(startIndex);
    }
  }, [isPlaying, animationPeriod, overlay, frames, currentFrameIndex]);

  const animationEnabled = isPlaying && Boolean(overlay) && frames.length > 0;

  useAnimationFrame((deltaTime) => {
    const animState = animationStateRef.current;
    const currentFrames = framesRef.current;
    if (!overlay || currentFrames.length === 0 || !animState) return;

    animState.msUntilNextFrame -= deltaTime;

    let didAdvance = false;
    while (animState.msUntilNextFrame <= 0) {
      animState.currentIndex =
        (animState.currentIndex + 1) % currentFrames.length;
      animState.msUntilNextFrame += getFrameDurationMs(
        animState.currentIndex,
        currentFrames,
        animationPeriod,
      );
      didAdvance = true;
    }

    if (didAdvance) {
      const epochSec =
        currentFrames[animState.currentIndex].timestamp.getTime() / 1000;
      overlay.setCurrentTime(epochSec);
      onFrameAdvanceRef.current(animState.currentIndex);
    }
  }, animationEnabled);
};
